/**
 * OrbitChatService — typed wrapper around OrbitService.js
 *
 * Provides a clean TypeScript surface for the streaming Orbit chat API.
 * Route: POST /api/clients/{clientId}/orbit/chat/stream  (SSE)
 *
 * SSE event envelope (from ai-chat-service):
 *   event: start  → { request_id, request_type }
 *   event: token  → { t: string }
 *   event: done   → { ok: true, final_text?: string, suggest_followup?: ..., clarification?: ... }
 *   event: error  → { message?: string, detail?: string }
 */

import OrbitServiceJS from './OrbitService';
import constant from '../constants/constant';

/** Replicates OrbitService.js makeHeaders — attaches the stored Bearer token. */
function makeHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const token = localStorage.getItem('access_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OrbitClient {
  ClientCode?: string | null;
  Name?:       string | null;
}

export interface OrbitChatUpload {
  filename: string;
  content_type: string;
  text: string;
  metadata?: Record<string, unknown>;
}

export interface OrbitChatDraft {
  message:          string;
  conversation_id?: string;
  idempotency_key?: string;
  context?:         Record<string, unknown>;
  metadata?:        Record<string, unknown>;
  uploads?:         OrbitChatUpload[];
}

export interface OrbitStartEvent {
  request_id:   string;
  request_type: string;
}

export interface OrbitTokenEvent {
  /** Short token — usually one word */
  t?: string;
  token?: string;
}

export interface OrbitDoneEvent {
  ok:               boolean;
  final_text?:      string;
  suggest_followup?: unknown;
  clarification?:   string;
}

export interface OrbitErrorEvent {
  message?: string;
  detail?:  string | Record<string, unknown>;
  /** Set to 'QUOTA_EXCEEDED' when the user's token budget is exhausted */
  error_code?: string;
}

type OrbitTransportError = Error & {
  orbitErrorCode?: string;
};

export type OrbitSseEvent =
  | { event: 'start';   data: OrbitStartEvent }
  | { event: 'token';   data: OrbitTokenEvent }
  | { event: 'done';    data: OrbitDoneEvent }
  | { event: 'error';   data: OrbitErrorEvent }
  | { event: string;    data: unknown };

export interface OrbitStreamHandlers {
  onStart?:  (e: OrbitStartEvent) => void;
  onToken?:  (token: string) => void;
  onDone?:   (e: OrbitDoneEvent) => void;
  onError?:  (e: OrbitErrorEvent) => void;
  onFinally?:() => void;
}

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * Start an SSE stream for a client message.
 * Returns an AbortController so callers can cancel mid-stream.
 */
export function streamOrbitChat(
  clientId: number,
  client:   OrbitClient,
  draft:    OrbitChatDraft,
  handlers: OrbitStreamHandlers,
): AbortController {
  const abort = new AbortController();

  const payload = OrbitServiceJS.buildPayload(client, draft);

  OrbitServiceJS.streamChat(clientId, payload, {
    onEvent: (event: OrbitSseEvent) => {
      if (abort.signal.aborted) return;

      if (event.event === 'start') {
        handlers.onStart?.(event.data as OrbitStartEvent);
      } else if (event.event === 'token') {
        const td = event.data as OrbitTokenEvent;
        const token = td.t ?? td.token ?? '';
        if (token) handlers.onToken?.(token);
      } else if (event.event === 'done') {
        handlers.onDone?.(event.data as OrbitDoneEvent);
      } else if (event.event === 'error') {
        handlers.onError?.(event.data as OrbitErrorEvent);
      }
    },
    onDone: () => {
      handlers.onFinally?.();
    },
  }).catch((err: unknown) => {
    if (abort.signal.aborted) return;
    const message = err instanceof Error ? err.message : 'Chat request failed';
    const errorCode = err instanceof Error && typeof (err as OrbitTransportError).orbitErrorCode === 'string'
      ? (err as OrbitTransportError).orbitErrorCode
      : undefined;
    handlers.onError?.({ message, error_code: errorCode });
    handlers.onFinally?.();
  });

  return abort;
}


// ── Feedback ──────────────────────────────────────────────────────────────────

export interface OrbitFeedbackPayload {
  conversation_id: string;
  message_id?:     number | null;
  thumbs:          1 | -1;
  feedback_text?:  string | null;
}

// ── Phase 2: Context chips ────────────────────────────────────────────────────

export interface OrbitContextChips {
  period?:     string | null;   // e.g. "2026-05" or "May 2026"
  department?: string | null;   // e.g. "General" or "Sportswear" (category/vertical)
  location?:   string | null;   // e.g. "all" or "Store 123"
  class?:      string | null;   // e.g. "Sneakers" — a narrower subdivision of department
}

export interface OrbitContextSummary {
  capability?: string;
  service?: string;
  client_name?: string;
  data_through?: string;
  overall_kpis?: Record<string, unknown>;
  location_count?: number;
  class_count?: number;
  include_current_month?: boolean;
}

export interface OrbitMaintenanceMessage {
  scope?: string;
  is_enabled: boolean;
  message: string;
  updated_at?: string | null;
  updated_by_user_id?: string | null;
}

// ── Phase 2: Conversation history ─────────────────────────────────────────────

export interface OrbitConversation {
  conversation_id:    string;
  status:             string;
    conversation_visibility?: 'private' | 'shared';
  request_type?:      string | null;
  timeframe?:         string | null;
  first_user_message?: string | null;
  last_message_at?:   string | null;
  created_at:         string;
}

export interface OrbitConversationMessage {
  message_id:   number;
  message_role: 'user' | 'assistant' | 'system';
  message_kind: string;
  content_text: string;
  created_at:   string;
}

export interface OrbitExecuteResponse {
  capability?: string;
  service?: string;
  message?: string;
  final_text?: string;
  conversation_id?: string;
  clarification?: string | null;
  suggest_followup?: unknown;
  result?: Record<string, unknown>;
}

export interface OrbitActionCardResult {
  num: number;
  title: string;
  subtitle: string;
  badge: string;
  badgeStyle: 'amber' | 'red' | 'purple' | 'green';
}

function extractNestedResult(data: unknown): Record<string, unknown> {
  const top = (data && typeof data === 'object') ? data as Record<string, unknown> : {};
  if (top.result && typeof top.result === 'object') {
    const nested = top.result as Record<string, unknown>;
    if (nested.result && typeof nested.result === 'object') {
      return nested.result as Record<string, unknown>;
    }
    return nested;
  }
  return top;
}

function createOrbitTransportError(message: string, errorCode?: string): OrbitTransportError {
  const error = new Error(message) as OrbitTransportError;
  if (errorCode) {
    error.orbitErrorCode = errorCode;
  }
  return error;
}

async function readOrbitErrorDetails(response: Response): Promise<{ message: string; errorCode?: string }> {
  try {
    const data = await response.json();
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return {
        message: detail.trim(),
        errorCode: typeof data?.error_code === 'string' ? data.error_code.trim() || undefined : undefined,
      };
    }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
      return {
        message: detail.message.trim(),
        errorCode: typeof detail.error_code === 'string' ? detail.error_code.trim() || undefined : undefined,
      };
    }
    if (typeof data?.message === 'string' && data.message.trim()) {
      return {
        message: data.message.trim(),
        errorCode: typeof data?.error_code === 'string' ? data.error_code.trim() || undefined : undefined,
      };
    }
  } catch {
    // ignore JSON parsing issues and fall back to the HTTP status below
  }

  return { message: `Orbit execute failed: ${response.status}` };
}

function stripJsonFences(text: string): string {
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return fenced ? fenced[1].trim() : trimmed;
}

function inferBadge(text: string): Pick<OrbitActionCardResult, 'badge' | 'badgeStyle'> {
  const source = text.toLowerCase();
  if (source.includes('markdown') || source.includes('mark down')) {
    return { badge: 'Markdown', badgeStyle: 'red' };
  }
  if (source.includes('inventory') || source.includes('receipt') || source.includes('stock')) {
    return { badge: 'Inventory', badgeStyle: 'purple' };
  }
  if (source.includes('margin') || source.includes('imu')) {
    return { badge: 'Margin', badgeStyle: 'green' };
  }
  if (source.includes('plan') || source.includes('priority') || source.includes('follow up')) {
    return { badge: 'Planning', badgeStyle: 'green' };
  }
  return { badge: 'Buying', badgeStyle: 'amber' };
}

function parseActionCards(text: string): OrbitActionCardResult[] {
  const cleaned = stripJsonFences(text);
  try {
    const parsed = JSON.parse(cleaned) as { actions?: Array<Record<string, unknown>> };
    if (Array.isArray(parsed.actions) && parsed.actions.length > 0) {
      return parsed.actions.slice(0, 6).map((action, index) => ({
        num: index + 1,
        title: String(action.title ?? '').trim() || `Priority action ${index + 1}`,
        subtitle: String(action.subtitle ?? '').trim(),
        badge: String(action.badge ?? 'Planning').trim() || 'Planning',
        badgeStyle: (['amber', 'red', 'purple', 'green'].includes(String(action.badge_style ?? '').trim())
          ? String(action.badge_style).trim()
          : inferBadge(`${action.title ?? ''} ${action.subtitle ?? ''}`).badgeStyle) as OrbitActionCardResult['badgeStyle'],
      }));
    }
  } catch {
    // fall through to line-based parsing
  }

  return cleaned
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 6)
    .map((line, index) => {
      const [title, subtitle = ''] = line.split(/\s+(?:—|-|:)\s+/, 2);
      const inferred = inferBadge(line);
      return {
        num: index + 1,
        title: title.trim() || `Priority action ${index + 1}`,
        subtitle: subtitle.trim(),
        badge: inferred.badge,
        badgeStyle: inferred.badgeStyle,
      };
    });
}

/**
 * Fetch the last N conversations for a client (History tab).
 */
export async function fetchOrbitConversations(
  clientId: number,
  limit    = 20,
): Promise<OrbitConversation[]> {
  let response: Response;
  try {
    response = await fetch(
      `${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/conversations?limit=${limit}`,
      { headers: makeHeaders() },
    );
  } catch (err) {
    console.warn('[OrbitChatService] fetchOrbitConversations network error:', err);
    return [];
  }
  if (!response.ok) {
    console.warn('[OrbitChatService] fetchOrbitConversations non-200:', response.status, response.statusText);
    return [];
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch (err) {
    console.warn('[OrbitChatService] fetchOrbitConversations JSON parse error:', err);
    return [];
  }
  // Gateway normalises the coordinator envelope — result may be top-level or nested
  const d = data as Record<string, unknown>;
  const list: OrbitConversation[] = Array.isArray(d?.conversations)
    ? (d.conversations as OrbitConversation[])
    : Array.isArray((d?.result as Record<string, unknown>)?.conversations)
      ? ((d.result as Record<string, unknown>).conversations as OrbitConversation[])
      : [];
  return list;
}

/**
 * Fetch all messages for a conversation thread (to resume in the chat UI).
 */
export async function fetchOrbitConversationMessages(
  clientId:       number,
  conversationId: string,
): Promise<OrbitConversationMessage[]> {
  const response = await fetch(
    `${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/conversations/${conversationId}/messages`,
    { headers: makeHeaders() },
  );
  if (!response.ok) return [];
  const data = await response.json();
  const messages: OrbitConversationMessage[] = Array.isArray(data?.messages)
    ? data.messages
    : Array.isArray(data?.result?.messages)
      ? data.result.messages
      : [];
  return messages;
}

/**
 * SPS-5637: the conversation rendered server-side as a formatted PDF
 * (markdown tables/bold/headings), returned as a Blob for the caller to save.
 */
export async function exportOrbitConversationPdf(
  clientId:       number,
  conversationId: string,
  timeZone?:      string,
): Promise<Blob> {
  // Message times are stored in UTC; the backend converts them into this IANA
  // zone (validated server-side, falling back to the app zone if absent/invalid).
  const query = timeZone ? `?tz=${encodeURIComponent(timeZone)}` : '';
  const response = await fetch(
    `${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/conversations/${encodeURIComponent(conversationId)}/export.pdf${query}`,
    { headers: makeHeaders({ Accept: 'application/pdf' }) },
  );
  if (!response.ok) {
    throw new Error(`Conversation export failed (${response.status})`);
  }
  return response.blob();
}

export async function updateOrbitConversationVisibility(
  clientId: number,
  conversationId: string,
  conversationVisibility: 'private' | 'shared',
): Promise<'private' | 'shared'> {
  const response = await fetch(
    `${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/conversations/${encodeURIComponent(conversationId)}/visibility`,
    {
      method: 'PATCH',
      headers: makeHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ conversation_visibility: conversationVisibility }),
    },
  );
  if (!response.ok) {
    const { message } = await readOrbitErrorDetails(response);
    throw new Error(message);
  }
  const data = await response.json();
  const result = extractNestedResult(data);
  return result.conversation_visibility === 'shared' ? 'shared' : 'private';
}

export async function fetchOrbitContextSummary(
  clientId: number,
  client: OrbitClient,
  context: Record<string, unknown> = {},
): Promise<OrbitContextSummary | null> {
  const response = await fetch(`${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/context/summary`, {
    method: 'POST',
    headers: makeHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(OrbitServiceJS.buildPayload(client, { context })),
  });
  if (!response.ok) return null;
  const data = await response.json();
  return extractNestedResult(data) as OrbitContextSummary;
}

export async function fetchOrbitMaintenanceMessage(
  clientId: number,
): Promise<OrbitMaintenanceMessage> {
  const response = await fetch(`${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/maintenance-message`, {
    headers: makeHeaders(),
  });

  if (!response.ok) {
    return {
      scope: 'global',
      is_enabled: false,
      message: '',
      updated_at: null,
      updated_by_user_id: null,
    };
  }

  const data = await response.json();
  const result = extractNestedResult(data) as Partial<OrbitMaintenanceMessage>;

  return {
    scope: typeof result.scope === 'string' && result.scope.trim() !== '' ? result.scope : 'global',
    is_enabled: result.is_enabled === true,
    message: typeof result.message === 'string' ? result.message : '',
    updated_at: typeof result.updated_at === 'string' ? result.updated_at : null,
    updated_by_user_id:
      typeof result.updated_by_user_id === 'string'
        ? result.updated_by_user_id.trim() || null
        : typeof result.updated_by_user_id === 'number'
          ? String(result.updated_by_user_id)
          : null,
  };
}

export async function executeOrbitChat(
  clientId: number,
  client: OrbitClient,
  draft: OrbitChatDraft,
): Promise<OrbitExecuteResponse> {
  const response = await fetch(`${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/chat/execute`, {
    method: 'POST',
    headers: makeHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(OrbitServiceJS.buildPayload(client, draft)),
  });
  if (!response.ok) {
    const { message, errorCode } = await readOrbitErrorDetails(response);
    throw createOrbitTransportError(message, errorCode);
  }
  const data = await response.json();
  return extractNestedResult(data) as OrbitExecuteResponse;
}

export async function generateOrbitActionCards(
  clientId: number,
  client: OrbitClient,
  options: {
    conversationId?: string;
    context?: Record<string, unknown>;
    recentMessages?: string[];
  } = {},
): Promise<OrbitActionCardResult[]> {
  const recentMessages = (options.recentMessages ?? []).filter(Boolean).slice(-3);
  const message = [
    'Generate the top 6 concise retail planning action cards for the right sidebar.',
    recentMessages.length ? `Recent conversation focus: ${recentMessages.join(' | ')}` : '',
    'Return ONLY valid JSON with shape {"actions":[{"title":"...","subtitle":"...","badge":"Buying","badge_style":"amber"}]}.',
    'Allowed badge values: Buying, Markdown, Inventory, Planning, Margin.',
    'Allowed badge_style values: amber, red, purple, green.',
    'Each title must be under 80 characters and each subtitle under 120 characters.',
  ].filter(Boolean).join('\n');

  const result = await executeOrbitChat(clientId, client, {
    message,
    conversation_id: options.conversationId,
    context: options.context,
  });

  const rawText = String(result.final_text ?? result.message ?? '').trim();
  if (!rawText) {
    return [];
  }
  return parseActionCards(rawText);
}

/**
 * POST /api/clients/{clientId}/orbit/chat/feedback
 * Fire-and-forget — the UI should not wait for this before updating.
 */
export async function submitOrbitFeedback(
  clientId: number,
  payload:  OrbitFeedbackPayload,
): Promise<void> {
  const response = await fetch(`${constant.BACKEND_DOMAIN}/api/clients/${clientId}/orbit/chat/feedback`, {
    method:  'POST',
    headers: makeHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify(payload),
  });
  if (!response.ok) {
    console.warn('[OrbitChatService] submitFeedback non-200', response.status);
  }
}

